/**
 * The member list (prototype drawMembers): hoisted roles first, then
 * Online, then Offline. Presence comes only from the gateway. The rows show
 * no voice icons (mute, deafen, can't speak): the owner asked for that
 * (2026-10-29); the sidebar's voice rows, the voice room and the profile
 * card show them. A person streaming or with the camera on shows LIVE and
 * the camera mark after the name (screen share and video, 2026-10-29).
 */
import { Permissions, type Channel, type GuildMember, type Snowflake } from '@neosocial/protocol';
import { memo, useMemo } from 'react';
import { useT } from '../i18n';
import { useData } from '../store/data';
import {
  can,
  channelPermissions,
  displayName,
  hoistRole,
  presenceOf,
  sortedRoles,
} from '../store/selectors';
import { Avatar, Icon } from '../ui/kit';
import { rememberAnchor } from '../ui/overlay';
import { useNameStyle } from './Message';
import { openProfile } from './ProfileCard';
import { openMemberMenu } from '../mod/memberMenu';
import { RoleIcon } from './RoleIcon';
import { MediaBadges, useMediaOf } from '../voice/VoiceFlags';
import { noteMenuItems, useStatusLine } from '../social/ProfileSocial';

/** A name cut by its ellipsis shows in full as the row's tooltip (§7.0 rule 4). */
function titleWhenCut(e: { currentTarget: HTMLElement }): void {
  const row = e.currentTarget;
  const n = row.querySelector<HTMLElement>('.mem-name');
  if (n && n.scrollWidth > n.clientWidth) row.title = n.textContent ?? '';
  else row.removeAttribute('title');
}

const MemberRow = memo(function MemberRow({ m, guildId }: { m: GuildMember; guildId: Snowflake }) {
  const { t } = useT();
  const p = useData((d) => presenceOf(d, m.user.id));
  // the custom status, else "Playing X" (rich presence, 2026-10-29)
  const custom = useStatusLine(m.user.id);
  const owner = useData((d) => d.guilds[guildId]?.owner_id === m.user.id);
  const style = useNameStyle(m.user.id, guildId);
  const name = displayName(m.user, m);
  // streaming or camera on: LIVE and the camera mark (screen share and video, §4.7)
  const media = useMediaOf(m.user.id, guildId);
  return (
    <button
      type="button"
      className="mem"
      role="listitem"
      aria-haspopup="dialog"
      aria-label={`${name}, ${t(`pres.${p}`)}${custom ? ', ' + custom : ''}`}
      onPointerEnter={titleWhenCut}
      onClick={(e) => openProfile(m.user.id, e.currentTarget, 'start-start')}
      onContextMenu={(e) => {
        e.preventDefault();
        const el = e.currentTarget;
        // View profile opens here even if the row has moved groups by then
        rememberAnchor(el);
        openMemberMenu({ x: e.clientX, y: e.clientY }, guildId, m.user.id, [
          {
            label: t('pres.profile'),
            icon: 'user',
            run: () => openProfile(m.user.id, el, 'start-start'),
          },
          // Discord's "Add Note": the profile opens with its note field ready
          ...noteMenuItems(m.user.id, () => openProfile(m.user.id, el, 'start-start')),
        ]);
      }}
    >
      <span className="mem-av">
        <Avatar user={m.user} name={name} size={32} presence={p} />
      </span>
      <span className="mem-t">
        <span className="mem-n">
          <span className={'mem-name' + (style ? ' nx-rc' : '')} style={style} dir="auto">
            {name}
          </span>
          <RoleIcon guildId={guildId} userId={m.user.id} cls="mem-ri" />
          {owner ? (
            <span className="mem-ic crown" data-tip={t('hub.mem.owner')}>
              <Icon name="crown" />
            </span>
          ) : null}
          {m.user.bot ? (
            <span className="msg-bot sm">
              <Icon name="check" />
              {t('hub.msg.bot')}
            </span>
          ) : null}
          {media.live || media.camera ? (
            <span className="mem-media">
              <MediaBadges live={media.live} camera={media.camera} cls="mem-ic" />
            </span>
          ) : null}
        </span>
        {custom && p !== 'offline' ? (
          <span className="mem-act">
            <span dir="auto">{custom}</span>
          </span>
        ) : null}
      </span>
    </button>
  );
});

export function MemberList({ guildId, channel }: { guildId: Snowflake; channel?: Channel }) {
  const { t, fmt } = useT();
  const members = useData((d) => d.members[guildId]);
  const guild = useData((d) => d.guilds[guildId]);
  const presences = useData((d) => d.presences);
  const groups = useMemo(() => {
    const d = { ...useData.getState(), presences };
    const roles = sortedRoles(guild).filter((r) => r.hoist && r.id !== guildId);
    const out = new Map<string, GuildMember[]>();
    for (const r of roles) out.set(r.id, []);
    out.set('online', []);
    out.set('offline', []);
    for (const m of Object.values(members ?? {})) {
      if (channel && !can(channelPermissions(d, channel, m.user.id), Permissions.VIEW_CHANNEL))
        continue;
      const p = presenceOf(d, m.user.id);
      let g = 'offline';
      if (p !== 'offline') g = hoistRole(guild, m)?.id ?? 'online';
      out.get(g)?.push(m);
    }
    for (const list of out.values())
      list.sort((a, b) => displayName(a.user, a).localeCompare(displayName(b.user, b)));
    return [...out.entries()]
      .filter(([, l]) => l.length)
      .map(([id, list]) => ({
        id,
        title:
          id === 'online'
            ? t('hub.mem.online')
            : id === 'offline'
              ? t('hub.mem.offline')
              : (roles.find((r) => r.id === id)?.name ?? ''),
        list,
      }));
    // presences change who is online
  }, [members, guild, channel, guildId, t, presences]);
  return (
    <div className="members nx-scroll" role="list" aria-label={t('hub.a11y.members')}>
      {groups.map((g) => (
        <section key={g.id} className={'mem-g' + (g.id === 'offline' ? ' off' : '')}>
          <h3>
            <span dir="auto">{g.title}</span>
            <span className="nx-num">{fmt.num(g.list.length)}</span>
          </h3>
          {g.list.map((m) => (
            <MemberRow key={m.user.id} m={m} guildId={guildId} />
          ))}
        </section>
      ))}
    </div>
  );
}
